import { useState, useCallback, useEffect, useRef } from 'react';
import { cmWorkflowClient } from '../../../infrastructure/CMWorkflowClient';
import { glowpickClient } from '../../../infrastructure/GlowpickClient';
import type { CMWorkflowItem } from '../../../models/CatalogManagement';
import { CMWorkflowStatus } from '../../../models/CatalogManagement';

interface PaginationInfo {
  hasNextPage: boolean;
  currentPage: number;
  itemsPerPage: number;
  lastEvaluatedKey: string | undefined;
}

interface SKUsByType {
  simple: number;
  variable: number;
  variation: number;
}

interface Statistics {
  totalByStatus: Record<string, number>;
  todayWPUploaded: number;
  todayCMRequested: number;
  todayPublished: number;
  thisWeekPublished: number;
  thisMonthPublished: number;
  totalPublishedSKUs: number;
  publishedSKUsByType: SKUsByType;
  totalDraftSKUs: number;
  draftSKUsByType: SKUsByType;
  waveCount: Record<string, number>;
}

const countSKUsByType = (workflow: CMWorkflowItem, skusByType: SKUsByType): number => {
  const skuMappings = workflow.skuCpiiMappings || [];
  skuMappings.forEach((mapping) => {
    const type = mapping.type?.toLowerCase();
    if (type === 'simple') {
      skusByType.simple++;
    } else if (type === 'variable') {
      skusByType.variable++;
    } else if (type === 'variation') {
      skusByType.variation++;
    }
  });
  return skuMappings.length || workflow.skus?.length || 0;
};

// No global flags - let React handle the lifecycle naturally

export const useCMWorkflowList = () => {
  const [workflows, setWorkflows] = useState<CMWorkflowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<Statistics>({
    totalByStatus: {},
    todayWPUploaded: 0,
    todayCMRequested: 0,
    todayPublished: 0,
    thisWeekPublished: 0,
    thisMonthPublished: 0,
    totalPublishedSKUs: 0,
    publishedSKUsByType: {
      simple: 0,
      variable: 0,
      variation: 0,
    },
    totalDraftSKUs: 0,
    draftSKUsByType: {
      simple: 0,
      variable: 0,
      variation: 0,
    },
    waveCount: {},
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    hasNextPage: false,
    currentPage: 1,
    itemsPerPage: 1000,
    lastEvaluatedKey: undefined,
  });

  const [filters, setFilters] = useState({
    month: '',
    cmStatus: '',
    gpi: '',
  });

  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoadingStats(true);

      const response = await cmWorkflowClient.getAllCMWorkflows();
      const allWorkflows = response.workflows;
      console.info('📊 Statistics: fetched', allWorkflows.length, 'workflows');

      const now = new Date();
      const today = now.toISOString().split('T')[0];

      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() + diff);
      weekStart.setHours(0, 0, 0, 0);
      const weekStartStr = weekStart.toISOString().split('T')[0] as string;

      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthStartStr = monthStart.toISOString().split('T')[0] as string;

      const totalByStatus: Record<string, number> = {};
      const waveCount: Record<string, number> = {};
      let todayWPUploaded = 0;
      let todayCMRequested = 0;
      let todayPublished = 0;
      let thisWeekPublished = 0;
      let thisMonthPublished = 0;
      let totalPublishedSKUs = 0;
      const publishedSKUsByType: SKUsByType = {
        simple: 0,
        variable: 0,
        variation: 0,
      };
      let totalDraftSKUs = 0;
      const draftSKUsByType: SKUsByType = {
        simple: 0,
        variable: 0,
        variation: 0,
      };

      allWorkflows.forEach((workflow) => {
        const status = workflow.lastHistory?.toStatus || workflow.cmStatus;
        const updatedDate = (workflow.lastHistory?.updatedAt || workflow.modifiedAt)?.split('T')[0];
        const wave = workflow.wave || 'Unknown';

        totalByStatus[status] = (totalByStatus[status] || 0) + 1;
        waveCount[wave] = (waveCount[wave] || 0) + 1;

        if (status === CMWorkflowStatus.CM_PUBLISHED) {
          totalPublishedSKUs += countSKUsByType(workflow, publishedSKUsByType);
        }

        if (status === CMWorkflowStatus.WP_UPLOADED) {
          totalDraftSKUs += countSKUsByType(workflow, draftSKUsByType);
        }

        if (!updatedDate) {
          return;
        }

        if (status === CMWorkflowStatus.WP_UPLOADED && updatedDate === today) {
          todayWPUploaded++;
        }
        if (status === CMWorkflowStatus.CM_REQUESTED && updatedDate === today) {
          todayCMRequested++;
        }
        if (status === CMWorkflowStatus.CM_PUBLISHED) {
          if (updatedDate === today) {
            todayPublished++;
          }
          if (updatedDate >= weekStartStr) {
            thisWeekPublished++;
          }
          if (updatedDate >= monthStartStr) {
            thisMonthPublished++;
          }
        }
      });

      setStatistics({
        totalByStatus,
        todayWPUploaded,
        todayCMRequested,
        todayPublished,
        thisWeekPublished,
        thisMonthPublished,
        totalPublishedSKUs,
        publishedSKUsByType,
        totalDraftSKUs,
        draftSKUsByType,
        waveCount,
      });
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchWorkflows = useCallback(async (reset = false) => {
    try {
      setLoading(true);

      const currentFilters = filtersRef.current;

      const hasFilters = Boolean(currentFilters.cmStatus || currentFilters.gpi);

      let newWorkflows: any[] = [];

      if (hasFilters) {
        // Use getCMWorkflowByParams when filters are applied
        const params: { cmStatus?: string; gpi?: string } = {};
        if (currentFilters.cmStatus) {
          params.cmStatus = currentFilters.cmStatus;
        }
        if (currentFilters.gpi) {
          params.gpi = currentFilters.gpi;
        }

        const result = await cmWorkflowClient.getCMWorkflowByParams(params);
        newWorkflows = Array.isArray(result) ? result : [];
      } else {
        // Use getAllCMWorkflows when no filters (get all)
        const response = await cmWorkflowClient.getAllCMWorkflows();
        newWorkflows = Array.isArray(response.workflows) ? response.workflows : [];

        setPagination((prev) => ({
          ...prev,
          hasNextPage: false,
          currentPage: 1,
          lastEvaluatedKey: undefined,
        }));
      }

      console.info('📋 Workflows: fetched', newWorkflows.length, 'workflows, reset:', reset);
      setWorkflows((prev) => {
        const updated = reset ? newWorkflows : [...prev, ...newWorkflows];
        console.info('📋 Workflows: state updated to', updated.length, 'workflows');
        return updated;
      });

      if (hasFilters) {
        // Reset pagination when using filters
        setPagination((prev) => ({
          ...prev,
          hasNextPage: false,
          currentPage: 1,
          lastEvaluatedKey: undefined,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch CM workflows:', error);
      if (reset) {
        setWorkflows([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (pagination.hasNextPage && !loading) {
      fetchWorkflows(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.hasNextPage, loading]);

  const refresh = useCallback(() => {
    fetchWorkflows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = useCallback((key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      lastEvaluatedKey: undefined,
    }));
  }, []);

  const setItemsPerPage = useCallback((itemsPerPage: number) => {
    setPagination((prev) => ({
      ...prev,
      itemsPerPage,
      currentPage: 1,
      lastEvaluatedKey: undefined,
    }));
    setTimeout(() => {
      fetchWorkflows(true);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialLoadRef = useRef(false);
  const isFirstRenderRef = useRef(true);

  // Initial load: fetch statistics and workflows once on mount
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      console.info('🚀 Initial load starting...');
      fetchStatistics();
      fetchWorkflows(true);
    } else {
      console.info('⏭️ Skipping initial load - already loaded');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch workflows when filters change (skip first render)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (initialLoadRef.current) {
      fetchWorkflows(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.cmStatus, filters.gpi, filters.month]);

  const deleteWorkflow = useCallback(
    async (gpi: string, cppi: string) => {
      try {
        await cmWorkflowClient.deleteCMWorkflow(gpi, cppi);
        console.info('✅ CM workflow deleted from API');

        try {
          await glowpickClient.deleteCpProductsFromGlowpickMetadata(gpi, cppi);
        } catch (metadataError) {
          console.warn('⚠️ Failed to update Glowpick metadata (non-critical):', metadataError);
        }

        setWorkflows((prev) => prev.filter((w) => !(w.gpi === gpi && w.cppi === cppi)));
        fetchStatistics();
        console.info('✅ Workflow deleted and UI updated');
      } catch (error) {
        console.error('Failed to delete workflow:', error);
        throw error;
      }
    },
    [fetchStatistics],
  );

  // Debug: log current state
  console.info('🔍 Hook state:', {
    workflowsCount: workflows.length,
    loading,
    loadingStats,
    filters,
  });

  return {
    workflows,
    loading,
    statistics,
    loadingStats,
    pagination,
    filters,
    loadMore,
    refresh,
    updateFilter,
    setItemsPerPage,
    refreshStatistics: fetchStatistics,
    deleteWorkflow,
  };
};
